import { useState, useEffect, useCallback } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-hot-toast";
import { request } from "../../utils/api";
import useCurrentUser from "../../hooks/useCurrentUser";
import { getSessionTenantId, getSessionUserId } from "../../utils/tenantContext";
import { toIsoDate, displayDateUS } from "../../utils/dateUtils";
import { isInvoiceEditLocked } from "./invoiceMarks";
import {
  CLINICAL_VISIT_TYPES,
  computeInvoiceTotal,
  INVOICE_VISIT_TYPE_OPTIONS,
  isFixedVisitType,
  isHourlyVisitType,
  isLegacyClinicalVisitType,
} from "./invoiceLineItem";

const PAGE_SIZE = 20;

/** Normalize date string to YYYY-MM-DD for comparison. */
const toYyyyMmDd = (date) => {
  if (!date) return "";
  try {
    let s = "";
    if (date instanceof Date) {
      if (Number.isNaN(date.getTime())) return "";
      s = new Intl.DateTimeFormat("en-CA").format(date);
    } else {
      s = String(date).split("T")[0].replace(/\//g, "-").trim();
    }

    const parts = s.split("-").map((p) => p.trim());
    if (parts.length !== 3) return s;

    if (parts[0].length === 4) {
      return `${parts[0]}-${parts[1].padStart(2, "0")}-${parts[2].padStart(2, "0")}`;
    }

    if (parts[2].length === 4) {
      const [m, d, y] = parts;
      return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
    }

    return s;
  } catch (e) {
    console.error("toYyyyMmDd error:", e);
    return String(date);
  }
};

const emptyFormDefaults = (companyName = "") => ({
  invoiceNumber: "",
  companyName: companyName || "",
  fromDate: "",
  toDate: "",
  dueDate: "",
  notes: "",
  newItemPatientName: "",
  newItemVisitDate: "",
  newItemVisitType: "",
  newItemHours: "",
  newItemRate: "",
});

export const useInvoice = ({
  enableRoleFilter = false,
  roleFilter = "",
} = {}) => {
  const [invoices, setInvoices] = useState([]);
  const [totalInvoices, setTotalInvoices] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentInvoiceItems, setCurrentInvoiceItems] = useState([]);
  const [editingInvoiceId, setEditingInvoiceId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showRateModal, setShowRateModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [visitTypeRates, setVisitTypeRates] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const {
    tenantId,
    userId,
    token,
    isAdminRole: isAdmin,
    companyName,
  } = useCurrentUser();

  /** Hourly/Fixed + existing clinical visit types; Manage Rates stays clinical-only. */
  const visitTypes = INVOICE_VISIT_TYPE_OPTIONS;
  const rateVisitTypes = CLINICAL_VISIT_TYPES;

  const methods = useForm({
    defaultValues: emptyFormDefaults(companyName),
  });

  const { watch, setValue, getValues, reset, trigger, setError, clearErrors } =
    methods;
  const watchVisitType = watch("newItemVisitType");

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(searchTerm), 500);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, roleFilter]);

  const fetchInvoices = useCallback(async () => {
    if (!token) return;
    try {
      const params = new URLSearchParams();
      params.append("limit", String(PAGE_SIZE));
      params.append("offset", String((currentPage - 1) * PAGE_SIZE));
      if (tenantId) params.append("tenantId", tenantId);
      if (debouncedSearch?.trim())
        params.append("search", debouncedSearch.trim());
      if (enableRoleFilter && roleFilter?.trim()) {
        params.append("role", roleFilter.trim());
      }

      const response = await request(`/invoice?${params.toString()}`);
      if (response.status === 1 && response.result?.invoices) {
        setInvoices(response.result.invoices);
        setTotalInvoices(response.result.total ?? response.result.invoices.length);
      }
    } catch (error) {
      // Logout / session clear — do not toast
      if (error?.isAuthError || error?.status === 401 || error?.message === "invalid token") {
        return;
      }
      console.error("Failed to fetch invoices", error);
      toast.error("Failed to fetch invoice history.");
    }
  }, [
    token,
    tenantId,
    debouncedSearch,
    enableRoleFilter,
    roleFilter,
    currentPage,
  ]);

  useEffect(() => {
    if (!token) return undefined;

    let cancelled = false;
    const fetchData = async () => {
      setIsLoading(true);
      try {
        try {
          let ratesUrl = "/invoice/rates";
          if (tenantId) {
            const params = new URLSearchParams();
            if (tenantId) params.append("tenantId", tenantId);
            if (userId) params.append("userId", userId);
            ratesUrl += `?${params.toString()}`;
          }

          const ratesResponse = await request(ratesUrl);
          if (cancelled) return;
          if (ratesResponse.status === 1 && ratesResponse.result?.rates) {
            const ratesMap = {};
            ratesResponse.result.rates.forEach((r) => {
              ratesMap[r.visitType] = r.rate;
            });
            setVisitTypeRates(ratesMap);
          }
        } catch (e) {
          if (e?.isAuthError || e?.status === 401 || e?.message === "invalid token") {
            return;
          }
          console.error("Failed to fetch rates", e);
        }

        if (!cancelled) await fetchInvoices();
      } catch (error) {
        if (error?.isAuthError || error?.status === 401 || error?.message === "invalid token") {
          return;
        }
        console.error("Error initializing invoice page:", error);
        toast.error("Failed to load initial data.");
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [fetchInvoices, tenantId, userId, token]);

  useEffect(() => {
    if (
      watchVisitType &&
      isLegacyClinicalVisitType(watchVisitType) &&
      visitTypeRates[watchVisitType]
    ) {
      setValue("newItemRate", visitTypeRates[watchVisitType]);
    }
  }, [watchVisitType, visitTypeRates, setValue]);

  useEffect(() => {
    if (!watchVisitType) return;
    if (isHourlyVisitType(watchVisitType)) {
      setValue("newItemHours", "");
      setValue("newItemRate", "");
    } else if (isFixedVisitType(watchVisitType)) {
      setValue("newItemHours", "");
      setValue("newItemRate", "");
    } else if (isLegacyClinicalVisitType(watchVisitType)) {
      setValue("newItemHours", "");
    }
  }, [watchVisitType, setValue]);

  const handleAddItem = async () => {
    clearErrors([
      "newItemPatientName",
      "newItemVisitDate",
      "newItemVisitType",
      "newItemHours",
      "newItemRate",
    ]);

    const values = getValues();
    const visitType = String(values.newItemVisitType || "").trim();
    const hourly = isHourlyVisitType(visitType);
    const fixed = isFixedVisitType(visitType);

    const fields = ["newItemPatientName", "newItemVisitDate", "newItemVisitType"];
    if (hourly) {
      fields.push("newItemHours", "newItemRate");
    } else if (fixed || isLegacyClinicalVisitType(visitType)) {
      fields.push("newItemRate");
    }

    const isValid = await trigger(fields);
    if (!isValid) {
      const requiredMsgs = {
        newItemPatientName: "Patient Name is required",
        newItemVisitDate: "Visit Date is required",
        newItemVisitType: "Visit Type is required",
        newItemHours: "Total Hours is required",
        newItemRate: hourly ? "Rate is required" : "Total is required",
      };
      const fresh = getValues();
      let firstError = "Please fill all required line item fields.";
      for (const name of fields) {
        const v = fresh[name];
        const empty =
          v === "" ||
          v === null ||
          v === undefined ||
          (typeof v === "string" && !v.trim());
        if (empty) {
          setError(name, { type: "manual", message: requiredMsgs[name] });
          if (firstError === "Please fill all required line item fields.") {
            firstError = requiredMsgs[name];
          }
        }
      }
      toast.error(firstError);
      return;
    }

    const latest = getValues();
    const {
      newItemPatientName,
      newItemVisitDate,
      newItemVisitType,
      newItemHours,
      newItemRate,
      fromDate,
      toDate,
    } = latest;

    if (!fromDate || !toDate) {
      if (!fromDate) {
        setError("fromDate", {
          type: "manual",
          message: "From Date is required",
        });
      }
      if (!toDate) {
        setError("toDate", {
          type: "manual",
          message: "To Date is required",
        });
      }
      toast.error("Please set the Invoice From Date and To Date first.");
      return;
    }

    const visitNorm = toYyyyMmDd(newItemVisitDate);
    const fromNorm = toYyyyMmDd(fromDate);
    const toNorm = toYyyyMmDd(toDate);

    if (visitNorm < fromNorm || visitNorm > toNorm) {
      setError("newItemVisitDate", {
        type: "manual",
        message: `Visit date must be between ${displayDateUS(fromDate)} and ${displayDateUS(toDate)}`,
      });
      toast.error(
        `Visit date must be between ${displayDateUS(fromDate)} and ${displayDateUS(toDate)}.`,
      );
      return;
    }

    const rate = parseFloat(newItemRate) || 0;
    const hours =
      hourly && newItemHours !== "" && newItemHours != null
        ? parseFloat(newItemHours) || 0
        : null;

    if (hourly && (!hours || hours <= 0)) {
      setError("newItemHours", {
        type: "manual",
        message: "Total Hours must be greater than 0",
      });
      toast.error("Total Hours must be greater than 0.");
      return;
    }

    const item = {
      id: Date.now(),
      patientName: String(newItemPatientName).trim(),
      visitDate: newItemVisitDate,
      visitType: String(newItemVisitType).trim(),
      rate,
      hours,
    };

    setCurrentInvoiceItems((prev) => [...prev, item]);
    clearErrors(fields);
    setValue("newItemPatientName", "");
    setValue("newItemVisitDate", "");
    setValue("newItemVisitType", "");
    setValue("newItemHours", "");
    setValue("newItemRate", "");
  };

  const handleRemoveItem = (itemId) => {
    setCurrentInvoiceItems((prev) => prev.filter((item) => item.id !== itemId));
  };

  const handleCloseCreateModal = () => {
    setCurrentInvoiceItems([]);
    setEditingInvoiceId(null);
    reset(emptyFormDefaults(getValues("companyName") || companyName));
    setShowCreateModal(false);
  };

  const openCreateModal = () => {
    setEditingInvoiceId(null);
    setCurrentInvoiceItems([]);
    reset(emptyFormDefaults(companyName));
    setShowCreateModal(true);
  };

  const openEditModal = (invoice, { force = false } = {}) => {
    if (!invoice) return;
    if (!force && isInvoiceEditLocked(invoice)) {
      toast.error("This invoice cannot be edited after Reviewed or Paid.");
      return;
    }
    setEditingInvoiceId(invoice.id);
    setCurrentInvoiceItems(
      (invoice.invoiceData || []).map((item, idx) => ({
        id: item.id || `line-${idx}`,
        patientName: item.patientName,
        visitDate: item.visitDate,
        visitType: item.visitType,
        rate: Number(item.rate) || 0,
        hours:
          item.hours != null && item.hours !== ""
            ? Number(item.hours)
            : null,
      })),
    );
    reset({
      ...emptyFormDefaults(invoice.companyName || companyName),
      invoiceNumber: invoice.invoiceNumber || "",
      companyName: invoice.companyName || companyName || "",
      fromDate: invoice.formDate ? toYyyyMmDd(invoice.formDate) : "",
      toDate: invoice.toDate ? toYyyyMmDd(invoice.toDate) : "",
      dueDate: invoice.dueDate ? toYyyyMmDd(invoice.dueDate) : "",
      notes: invoice.notes || "",
    });
    setShowCreateModal(true);
  };

  const buildPayload = () => {
    const { invoiceNumber, companyName: company, fromDate, toDate, dueDate, notes } =
      getValues();
    const sessionTenantId = getSessionTenantId();
    const employerId = getSessionUserId();

    const payload = {
      invoiceNumber,
      tenantId: sessionTenantId,
      employerId,
      companyName: company,
      formDate: toIsoDate(fromDate),
      toDate: toIsoDate(toDate),
      invoiceData: currentInvoiceItems.map((item) => {
        const row = {
          patientName: item.patientName,
          visitDate: toIsoDate(item.visitDate),
          visitType: item.visitType,
          rate: item.rate,
        };
        if (isHourlyVisitType(item.visitType)) {
          row.hours = item.hours ?? 0;
        }
        return row;
      }),
    };

    // Optional fields — omit empty on create; send null on edit to clear
    if (dueDate) {
      payload.dueDate = toIsoDate(dueDate);
    } else if (editingInvoiceId) {
      payload.dueDate = null;
    }

    const notesTrimmed = (notes || "").trim();
    if (notesTrimmed) {
      payload.notes = notesTrimmed;
    } else if (editingInvoiceId) {
      payload.notes = null;
    }

    return payload;
  };

  const handleSubmitInvoice = async () => {
    const isValid = await trigger([
      "invoiceNumber",
      "companyName",
      "fromDate",
      "toDate",
    ]);
    if (!isValid) {
      const values = getValues();
      const detailMsgs = {
        invoiceNumber: "Invoice Number is required",
        companyName: "Company Name is required",
        fromDate: "From Date is required",
        toDate: "To Date is required",
      };
      for (const [name, message] of Object.entries(detailMsgs)) {
        const v = values[name];
        if (v === "" || v === null || v === undefined) {
          setError(name, { type: "manual", message });
        }
      }
      toast.error("Please fill all required invoice details.");
      return;
    }

    if (currentInvoiceItems.length === 0) {
      toast.error("Please add at least one line item before creating the invoice.");
      return;
    }

    const badItem = currentInvoiceItems.find((item) => {
      if (
        !item.patientName?.trim() ||
        !item.visitDate ||
        !item.visitType?.trim() ||
        item.rate === "" ||
        item.rate === null ||
        item.rate === undefined
      ) {
        return true;
      }
      if (isHourlyVisitType(item.visitType)) {
        return !item.hours || Number(item.hours) <= 0;
      }
      return false;
    });
    if (badItem) {
      toast.error(
        "Each line item needs Patient Name, Visit Date, Visit Type, and amount details.",
      );
      return;
    }

    setIsLoading(true);
    try {
      const payload = buildPayload();
      if (!payload.tenantId || !payload.employerId) {
        toast.error("Session context is missing. Please sign in again.");
        setIsLoading(false);
        return;
      }

      const isEdit = Boolean(editingInvoiceId);
      const response = await request(
        isEdit ? `/invoice/${editingInvoiceId}` : "/invoice",
        {
          method: isEdit ? "PUT" : "POST",
          body: JSON.stringify(payload),
        },
      );

      if (response.status === 1) {
        toast.success(
          isEdit ? "Invoice updated successfully!" : "Invoice created successfully!",
        );
        await fetchInvoices();
        handleCloseCreateModal();
      } else {
        toast.error(
          response.message ||
            (isEdit ? "Failed to update invoice." : "Failed to create invoice."),
        );
      }
    } catch (error) {
      console.error("Save invoice error:", error);
      const apiMessage =
        error?.response?.data?.message ||
        error?.message ||
        "An error occurred while saving the invoice.";
      toast.error(apiMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteInvoice = async (invoiceId) => {
    try {
      const response = await request(`/invoice/${invoiceId}`, {
        method: "DELETE",
      });

      if (response.status === 1) {
        toast.success("Invoice deleted successfully");
        await fetchInvoices();
        return true;
      }
      toast.error(response.message || "Failed to delete invoice");
      return false;
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to delete invoice");
      return false;
    }
  };

  const handleApplyMarks = async (invoiceIds, markType, action = "apply") => {
    if (!invoiceIds?.length) {
      toast.error("Select at least one invoice");
      return false;
    }
    const isRemove = action === "remove";
    try {
      const response = await request("/invoice/marks", {
        method: "POST",
        body: JSON.stringify({
          invoiceIds,
          markType,
          action: isRemove ? "remove" : "apply",
        }),
      });
      if (response.status === 1) {
        const updated = response.result?.updated ?? 0;
        toast.success(
          updated > 0
            ? isRemove
              ? `${markType} removed from ${updated} invoice${updated === 1 ? "" : "s"}`
              : `${markType} applied to ${updated} invoice${updated === 1 ? "" : "s"}`
            : response.message || "No invoices updated",
        );
        await fetchInvoices();
        return true;
      }
      toast.error(response.message || "Failed to update flags");
      return false;
    } catch (error) {
      console.error("Apply marks error:", error);
      toast.error("Failed to update flags");
      return false;
    }
  };

  const handleSaveRates = async (updatedRates) => {
    try {
      const payload = {
        rates: Object.entries(updatedRates).map(([visitType, rate]) => ({
          visitType,
          rate: parseFloat(rate),
        })),
      };

      const response = await request("/invoice/rates", {
        method: "POST",
        body: JSON.stringify(payload),
      });

      if (response.status === 1) {
        toast.success("Rates saved successfully");
        setVisitTypeRates(updatedRates);
        setShowRateModal(false);
      } else {
        toast.error("Failed to save rates");
      }
    } catch (error) {
      console.error("Save rates error", error);
      toast.error("Error saving rates");
    }
  };

  const totalPages = Math.max(1, Math.ceil(totalInvoices / PAGE_SIZE) || 1);

  return {
    methods,
    invoices,
    totalInvoices,
    currentPage,
    setCurrentPage,
    pageSize: PAGE_SIZE,
    totalPages,
    currentInvoiceItems,
    editingInvoiceId,
    isLoading,
    showRateModal,
    setShowRateModal,
    showCreateModal,
    setShowCreateModal,
    openCreateModal,
    openEditModal,
    handleCloseCreateModal,
    visitTypeRates,
    visitTypes,
    rateVisitTypes,
    isAdmin,
    handleAddItem,
    handleRemoveItem,
    handleSubmitInvoice,
    handleDeleteInvoice,
    handleApplyMarks,
    handleSaveRates,
    fetchInvoices,
    searchTerm,
    setSearchTerm,
    totalCurrentAmount: computeInvoiceTotal(currentInvoiceItems),
  };
};
